import { cs } from "../../utils/currency";
import React, { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, X, Upload } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

enum DealType {
  FixedPrice = "fixed_price",
  PercentageDiscount = "percentage_discount",
  BuyXGetY = "buy_x_get_y",
  BuildYourOwn = "build_your_own",
  Bundle = "bundle",
}

interface DealItem {
  itemId: string;
  quantity: number;
  isFree?: boolean;
}

interface Deal {
  _id: string;
  name: string;
  description?: string;
  dealType: DealType;
  items: DealItem[];
  dealPrice?: number;
  discountPercent?: number;
  minOrderAmount?: number;
  maxUsagePerOrder?: number;
  validFrom: string;
  validTill: string;
  isActive: boolean;
  createdBy?: string;
  imageUrl?: string;
  // build_your_own fields
  basePrice?: number;
  includedToppings?: number;
  minPizzas?: number;
  // bundle fields
  bundlePrice?: number;
  bundleSlots?: { options: string[]; preselections: { groupName: string; choiceLabel: string }[] }[];
}

interface CustomizeChoice {
  label: string;
  price: number;
}
interface CustomizeGroup {
  name: string;
  choices: CustomizeChoice[];
}
interface MenuItem {
  _id: string;
  name: string;
  price: number;
  category: string;
  customize?: CustomizeGroup[];
}

interface Category {
  _id: string;
  name: string;
}

const Deals: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [editingItem, setEditingItem] = useState<Deal | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    dealType: "",
    items: [] as DealItem[],
    dealPrice: "",
    discountPercent: "",
    minOrderAmount: "",
    maxUsagePerOrder: "1",
    validFrom: "",
    validTill: "",
    isActive: true,
    imageUrl: "",
    // build_your_own fields
    basePrice: "",
    includedToppings: "0",
    minPizzas: "1",
    // bundle fields
    bundlePrice: "",
    bundleSlots: [] as { options: string[]; preselections: { groupName: string; choiceLabel: string }[] }[],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const getSelectedItemIds = (currentIndex: number) => {
    return formData.items
      .filter((_, i) => i !== currentIndex)
      .map((i) => i.itemId)
      .filter(Boolean);
  };

  useEffect(() => {
    if (formData.dealType === DealType.FixedPrice) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.map((i) => ({ ...i, isFree: false })),
      }));
    }
  }, [formData.dealType]);

  useEffect(() => {
    fetchDeals();
    fetchMenuItems();
  }, []);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("admin/getDeals?all=true");
      if (response.data.success && Array.isArray(response.data.data)) {
        setDeals(response.data.data);
      }
    } catch {
      setError("Failed to load deals. Please try again.");
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await api.get("/menu/pizzaitems");
      if (response.data.success) {
        setMenuItems(response.data.data);
      }
    } catch {
      console.error("Failed to fetch menu items");
    }
  };

  useEffect(() => {
    if (formData.dealType === DealType.PercentageDiscount) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.slice(0, 1).map((i) => ({
          ...i,
          isFree: false,
        })),
      }));
    }
  }, [formData.dealType]);

  const handleImageUpload = async (file: File): Promise<string> => {
    const formDataObj = new FormData();
    formDataObj.append("image", file);

    setUploading(true);

    try {
      const response = await api.post<{ url: string }>(
        "/upload-image",
        formDataObj,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.url;
    } catch (err) {
      console.error("Error uploading image", err);
      throw new Error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that at least one item is added
    // if (formData.items.length === 0) {
    //   setError("Please add at least one item to the deal");
    //   toast.error("Please add at least one item to the deal");
    //   return;
    // }

    // Validate deal type specific fields
    if (formData.dealType === DealType.FixedPrice && !formData.dealPrice) {
      setError("Deal price is required for Fixed Price deals");
      toast.error("Deal price is required for Fixed Price deals");
      return;
    }

    if (
      formData.dealType === DealType.PercentageDiscount &&
      !formData.discountPercent
    ) {
      setError("Discount percentage is required for Percentage Discount deals");
      toast.error(
        "Discount percentage is required for Percentage Discount deals",
      );
      return;
    }

    if (formData.dealType === DealType.PercentageDiscount) {
      if (formData.items.length !== 1) {
        toast.error("Percentage Discount must have exactly one item");
        return;
      }
    }

    if (formData.dealType === DealType.BuyXGetY) {
      if (formData.items.length !== 2) {
        toast.error("Buy X Get Y must have exactly 2 items");
        return;
      }

      if (!formData.items[1].isFree) {
        toast.error("Second item must be free");
        return;
      }
    }

    if (formData.dealType === DealType.BuildYourOwn) {
      if (!formData.basePrice) {
        toast.error("Base price is required for Build Your Own deals");
        return;
      }
    }

    if (formData.dealType === DealType.Bundle) {
      if (!formData.bundlePrice) {
        toast.error("Bundle price is required for Bundle deals");
        return;
      }
      if (formData.bundleSlots.length === 0) {
        toast.error("Bundle deals must have at least one slot for the customer to customize");
        return;
      }
      if (formData.bundleSlots.some((s) => s.options.length === 0)) {
        toast.error("Each bundle slot must have at least one item option");
        return;
      }
    }

    try {
      setError(null);

      // Upload image if a new file is selected
      let imageUrl = formData.imageUrl;
      if (file) {
        imageUrl = await handleImageUpload(file);
      }

      const dealData = {
        name: formData.name,
        description: formData.description || undefined,
        dealType: formData.dealType,
        items: formData.items,
        dealPrice: formData.dealPrice
          ? parseFloat(formData.dealPrice)
          : undefined,
        discountPercent: formData.discountPercent
          ? parseFloat(formData.discountPercent)
          : undefined,
        minOrderAmount: formData.minOrderAmount
          ? parseFloat(formData.minOrderAmount)
          : 0,
        maxUsagePerOrder: formData.maxUsagePerOrder
          ? parseInt(formData.maxUsagePerOrder)
          : 1,
        validFrom: new Date(formData.validFrom).toISOString(),
        validTill: new Date(formData.validTill).toISOString(),
        isActive: formData.isActive,
        imageUrl: imageUrl,
        // build_your_own fields
        ...(formData.dealType === DealType.BuildYourOwn && {
          basePrice: parseFloat(formData.basePrice),
          includedToppings: parseInt(formData.includedToppings) || 0,
          minPizzas: parseInt(formData.minPizzas) || 1,
        }),
        // bundle fields
        ...(formData.dealType === DealType.Bundle && {
          bundlePrice: parseFloat(formData.bundlePrice),
          bundleSlots: formData.bundleSlots.map((slot) => ({
            options: slot.options,
            preselections: slot.preselections.filter((p) => p.groupName && p.choiceLabel),
          })),
        }),
      };

      if (editingItem) {
        // Update existing deal
        await api.put(`admin/updateDeal/${editingItem._id}`, dealData);
        toast.success("Deal updated successfully!");
      } else {
        // Create new deal using the admin endpoint
        await api.post("/admin/createDeal", dealData);
        toast.success("Deal added successfully!");
      }
      await fetchDeals();
      resetForm();
      setShowModal(false);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to save deal. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Error saving deal:", err);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFile(null);
    setFormData({
      name: "",
      description: "",
      dealType: "",
      items: [],
      dealPrice: "",
      discountPercent: "",
      minOrderAmount: "",
      maxUsagePerOrder: "1",
      validFrom: "",
      validTill: "",
      isActive: true,
      imageUrl: "",
      basePrice: "",
      includedToppings: "0",
      minPizzas: "1",
      bundlePrice: "",
      bundleSlots: [] as { options: string[]; preselections: { groupName: string; choiceLabel: string }[] }[],
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this deal?")) {
      try {
        setError(null);
        await api.delete(`admin/deleteDeal/${id}`);
        await fetchDeals();
        toast.success("Deal deleted successfully!");
      } catch {
        setError("Failed to delete deal. Please try again.");
        toast.error("Failed to delete deal.");
      }
    }
  };

  useEffect(() => {
    if (formData.dealType === DealType.BuyXGetY) {
      setFormData((prev) => {
        const items = prev.items.slice(0, 2).map((item, index) => ({
          ...item,
          isFree: index === 1,
        }));

        return { ...prev, items };
      });
    }
  }, [formData.dealType]);

  const handleEdit = (item: Deal) => {
    setEditingItem(item);
    setFile(null);
    setFormData({
      name: item.name,
      description: item.description || "",
      dealType: item.dealType,
      // Normalize populated objects to plain IDs (server now populates items.itemId and bundleChoices)
      items: item.items.map((di: any) => ({
        itemId: typeof di.itemId === 'object' && di.itemId !== null ? di.itemId._id : di.itemId,
        quantity: di.quantity,
        isFree: di.isFree ?? false,
      })),
      dealPrice: item.dealPrice?.toString() || "",
      discountPercent: item.discountPercent?.toString() || "",
      minOrderAmount: item.minOrderAmount?.toString() || "",
      maxUsagePerOrder: item.maxUsagePerOrder?.toString() || "1",
      validFrom: item.validFrom.split("T")[0],
      validTill: item.validTill.split("T")[0],
      isActive: item.isActive,
      imageUrl: item.imageUrl || "",
      basePrice: item.basePrice?.toString() || "",
      includedToppings: item.includedToppings?.toString() ?? "0",
      minPizzas: item.minPizzas?.toString() || "1",
      bundlePrice: item.bundlePrice?.toString() || "",
      bundleSlots: (item.bundleSlots || []).map((slot: any) => ({
        options: (slot.options || []).map((o: any) =>
          typeof o === 'object' && o !== null ? o._id : o
        ),
        preselections: (slot.preselections || []).map((p: any) => ({
          groupName: p.groupName || '',
          choiceLabel: p.choiceLabel || '',
        })),
      })),
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const addDealItem = () => {
    if (
      formData.dealType === DealType.PercentageDiscount &&
      formData.items.length >= 1
    ) {
      toast.error("Percentage Discount allows only one item");
      return;
    }

    if (formData.dealType === DealType.BuyXGetY && formData.items.length >= 2) {
      toast.error("Buy X Get Y deals can have maximum 2 items");
      return;
    }


    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          itemId: "",
          quantity: 1,
          isFree:
            formData.dealType === DealType.BuyXGetY &&
            formData.items.length === 1,
        },
      ],
    });
  };

  const removeDealItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateDealItem = (index: number, field: keyof DealItem, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const filteredItems = deals.filter((item) => {
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const getDealTypeLabel = (type: string) => {
    switch (type) {
      case DealType.FixedPrice:
        return "Fixed Price";
      case DealType.PercentageDiscount:
        return "Percentage Discount";
      case DealType.BuyXGetY:
        return "Buy X Get Y";
      case DealType.BuildYourOwn:
        return "Build Your Own Pizza";
      case DealType.Bundle:
        return "Bundle Deal";
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col overflow-y-auto h-screen">
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Deals</h1>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2 whitespace-nowrap"
            >
              <Plus size={18} />
              Add Deal
            </button>
          </div>
        </div>

        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Deal
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by name..."
              className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      <div className="flex-1">
        {paginatedItems.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No deals found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first deal to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paginatedItems.map((item) => (
              <div
                key={item._id}
                className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col h-full"
              >
                <div className="aspect-square w-full h-48 bg-gray-200 relative overflow-hidden flex-shrink-0">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Upload size={32} />
                    </div>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-grow">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-gray-800 line-clamp-1">
                      {item.name}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${item.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                    >
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-2">
                    {getDealTypeLabel(item.dealType)}
                  </p>

                  {item.description && (
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="text-sm text-gray-600 mb-2">
                    <p>Items: {item.items.length}</p>
                    {item.dealPrice && (
                      <p>Price: ${item.dealPrice.toFixed(2)}</p>
                    )}
                    {item.discountPercent && (
                      <p>Discount: {item.discountPercent}%</p>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mb-3">
                    <p>
                      Valid: {new Date(item.validFrom).toLocaleDateString()} -{" "}
                      {new Date(item.validTill).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => handleEdit(item)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {filteredItems.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded transition ${
                  currentPage === page
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {editingItem ? "Edit Deal" : "Add New Deal"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form id="deal-form" onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Deal Name
                      </label>
                      <input
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        type="text"
                        placeholder="e.g., Summer Special"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Deal Type
                      </label>
                      <select
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                        value={formData.dealType}
                        onChange={(e) =>
                          setFormData({ ...formData, dealType: e.target.value })
                        }
                        required
                      >
                        <option value="">Select deal type</option>
                        <option value={DealType.FixedPrice}>Fixed Price</option>
                        <option value={DealType.PercentageDiscount}>
                          Percentage Discount
                        </option>
                        <option value={DealType.BuyXGetY}>Buy X Get Y</option>
                        <option value={DealType.BuildYourOwn}>
                          Build Your Own Pizza
                        </option>
                        <option value={DealType.Bundle}>Bundle Deal</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      rows={3}
                      placeholder="Describe your deal..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Deal Type Specific Fields */}
                  {formData.dealType && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h3 className="text-sm font-semibold text-blue-900 mb-3">
                        {getDealTypeLabel(formData.dealType)} Settings
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {formData.dealType === DealType.FixedPrice && (
                          <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              Deal Price <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                $
                              </span>
                              <input
                                className="w-full p-2.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                placeholder="0.00"
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={formData.dealPrice}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    dealPrice: e.target.value,
                                  })
                                }
                                required={
                                  formData.dealType === DealType.FixedPrice
                                }
                              />
                            </div>
                          </div>
                        )}

                        {formData.dealType === DealType.PercentageDiscount && (
                          <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              Discount Percentage{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                className="w-full p-2.5 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                placeholder="0"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={formData.discountPercent}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    discountPercent: e.target.value,
                                  })
                                }
                                required={
                                  formData.dealType ===
                                  DealType.PercentageDiscount
                                }
                              />
                              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                %
                              </span>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Min Order Amount (Optional)
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                              $
                            </span>
                            <input
                              className="w-full p-2.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              placeholder="0.00"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.minOrderAmount}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  minOrderAmount: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* Build Your Own specific settings */}
                      {formData.dealType === DealType.BuildYourOwn && (
                        <div className="mt-4 space-y-4 border-t border-blue-200 pt-4">
                          <h4 className="text-sm font-semibold text-blue-800">Pizza Configuration</h4>
                          <p className="text-xs text-gray-500">
                            The customer will customize the pizza using the item's existing options (toppings, sizes, etc).
                            Toppings are the item's own customizable choices.
                          </p>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <label className="block mb-2 text-sm font-medium text-gray-700">
                                Base Price (per pizza) <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                                <input
                                  className="w-full p-2.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="10.99"
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  value={formData.basePrice}
                                  onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block mb-2 text-sm font-medium text-gray-700">
                                Included Toppings (free)
                              </label>
                              <input
                                className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                type="number"
                                min="0"
                                placeholder="5"
                                value={formData.includedToppings}
                                onChange={(e) => setFormData({ ...formData, includedToppings: e.target.value })}
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                First N toppings are free. Beyond that, item's own price applies.
                              </p>
                            </div>
                            <div>
                              <label className="block mb-2 text-sm font-medium text-gray-700">
                                Min Pizzas Required
                              </label>
                              <input
                                className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                type="number"
                                min="1"
                                placeholder="1"
                                value={formData.minPizzas}
                                onChange={(e) => setFormData({ ...formData, minPizzas: e.target.value })}
                              />
                              <p className="text-xs text-gray-500 mt-1">Set 2 for "2 or More" deals</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bundle specific settings */}
                      {formData.dealType === DealType.Bundle && (
                        <div className="mt-4 space-y-4 border-t border-blue-200 pt-4">
                          <h4 className="text-sm font-semibold text-blue-800">Bundle Configuration</h4>
                          <p className="text-xs text-gray-500">
                            Set a total bundle price and define customizable slots. Each slot = one item the customer picks and customizes (e.g. "choose any platter"). Add fixed sides/drinks in the "Deal Items" section below.
                          </p>

                          {/* Bundle price */}
                          <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              Bundle Price (total) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative max-w-xs">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                              <input
                                className="w-full p-2.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="23.99"
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={formData.bundlePrice}
                                onChange={(e) => setFormData({ ...formData, bundlePrice: e.target.value })}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Customer pays this total. Extra toppings are charged on top.
                            </p>
                          </div>

                          {/* Bundle Slots */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">
                                Customizable Slots <span className="text-red-500">*</span>
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    bundleSlots: [...prev.bundleSlots, { options: [], preselections: [] }],
                                  }))
                                }
                                className="flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-300 rounded px-2 py-1 hover:bg-blue-50"
                              >
                                <Plus size={12} /> Add Slot
                              </button>
                            </div>

                            {formData.bundleSlots.length === 0 && (
                              <p className="text-xs text-gray-400 italic">No slots yet. Click "Add Slot" to add the first customizable item.</p>
                            )}

                            {formData.bundleSlots.map((slot, slotIdx) => (
                              <div key={slotIdx} className="border border-blue-200 rounded-lg mb-3 overflow-hidden">
                                {/* Slot header */}
                                <div className="flex items-center justify-between bg-blue-50 px-3 py-2 border-b border-blue-200">
                                  <span className="text-xs font-bold text-blue-800">
                                    Slot {slotIdx + 1} — {slot.options.length > 0 ? `${slot.options.length} option${slot.options.length > 1 ? "s" : ""}` : "no items selected"}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        bundleSlots: prev.bundleSlots.filter((_, i) => i !== slotIdx),
                                      }))
                                    }
                                    className="text-red-400 hover:text-red-600"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>

                                {/* Item options */}
                                <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                                  {menuItems.length === 0 && (
                                    <p className="text-xs text-gray-400 p-3">No menu items loaded.</p>
                                  )}
                                  {menuItems.map((mi) => {
                                    const checked = slot.options.includes(mi._id);
                                    return (
                                      <label
                                        key={mi._id}
                                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => {
                                            const next = checked
                                              ? slot.options.filter((id) => id !== mi._id)
                                              : [...slot.options, mi._id];
                                            setFormData((prev) => ({
                                              ...prev,
                                              bundleSlots: prev.bundleSlots.map((s, i) =>
                                                i === slotIdx ? { ...s, options: next } : s
                                              ),
                                            }));
                                          }}
                                          className="w-4 h-4 accent-blue-600 rounded"
                                        />
                                        <span className="text-sm text-gray-800">{mi.name}</span>
                                        <span className="ml-auto text-xs text-gray-400">{cs()}{mi.price.toFixed(2)}</span>
                                      </label>
                                    );
                                  })}
                                </div>

                                {/* Preset selections — dynamic dropdowns from selected items' customize groups */}
                                {(() => {
                                  // Build customize groups from all items selected in this slot
                                  const slotItems = slot.options
                                    .map((id) => menuItems.find((m) => m._id === id))
                                    .filter(Boolean) as MenuItem[];
                                  const groupMap: Record<string, string[]> = {};
                                  slotItems.forEach((item) => {
                                    (item.customize || []).forEach((grp) => {
                                      if (!groupMap[grp.name]) groupMap[grp.name] = [];
                                      grp.choices.forEach((c) => {
                                        if (!groupMap[grp.name].includes(c.label))
                                          groupMap[grp.name].push(c.label);
                                      });
                                    });
                                  });
                                  const groupNames = Object.keys(groupMap);
                                  const hasCustomizeData = groupNames.length > 0;

                                  return (
                                    <div className="border-t border-blue-100 bg-blue-50/50 px-3 py-2">
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-[11px] font-semibold text-blue-700">Auto-Select Options</span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setFormData((prev) => ({
                                              ...prev,
                                              bundleSlots: prev.bundleSlots.map((s, i) =>
                                                i === slotIdx
                                                  ? { ...s, preselections: [...s.preselections, { groupName: '', choiceLabel: '' }] }
                                                  : s
                                              ),
                                            }))
                                          }
                                          className="text-[11px] text-blue-600 font-semibold hover:underline"
                                        >
                                          + Add
                                        </button>
                                      </div>
                                      {slot.preselections.length === 0 && (
                                        <p className="text-[10px] text-gray-400 italic">
                                          {hasCustomizeData
                                            ? 'Pick a group + choice to auto-select when this slot opens (e.g. Size → XL)'
                                            : slot.options.length === 0
                                              ? 'Select items above to see available options'
                                              : 'No customize options found on selected items'}
                                        </p>
                                      )}
                                      {slot.preselections.map((preset, presetIdx) => (
                                        <div key={presetIdx} className="flex items-center gap-1.5 mb-1">
                                          {/* Group dropdown */}
                                          <select
                                            value={preset.groupName}
                                            onChange={(e) => {
                                              const newGroup = e.target.value;
                                              setFormData((prev) => ({
                                                ...prev,
                                                bundleSlots: prev.bundleSlots.map((s, i) =>
                                                  i === slotIdx
                                                    ? { ...s, preselections: s.preselections.map((p, pi) => pi === presetIdx ? { groupName: newGroup, choiceLabel: '' } : p) }
                                                    : s
                                                ),
                                              }));
                                            }}
                                            className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                                          >
                                            <option value="">Group...</option>
                                            {groupNames.map((g) => (
                                              <option key={g} value={g}>{g}</option>
                                            ))}
                                          </select>
                                          {/* Choice dropdown — filtered by selected group */}
                                          <select
                                            value={preset.choiceLabel}
                                            onChange={(e) =>
                                              setFormData((prev) => ({
                                                ...prev,
                                                bundleSlots: prev.bundleSlots.map((s, i) =>
                                                  i === slotIdx
                                                    ? { ...s, preselections: s.preselections.map((p, pi) => pi === presetIdx ? { ...p, choiceLabel: e.target.value } : p) }
                                                    : s
                                                ),
                                              }))
                                            }
                                            className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                                          >
                                            <option value="">Choice...</option>
                                            {(groupMap[preset.groupName] || []).map((label) => (
                                              <option key={label} value={label}>{label}</option>
                                            ))}
                                          </select>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setFormData((prev) => ({
                                                ...prev,
                                                bundleSlots: prev.bundleSlots.map((s, i) =>
                                                  i === slotIdx
                                                    ? { ...s, preselections: s.preselections.filter((_, pi) => pi !== presetIdx) }
                                                    : s
                                                ),
                                              }))
                                            }
                                            className="text-red-400 hover:text-red-600 flex-shrink-0"
                                          >
                                            <X size={12} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Max Usage Per Order
                      </label>
                      <input
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        type="number"
                        min="1"
                        value={formData.maxUsagePerOrder}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            maxUsagePerOrder: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Valid From
                      </label>
                      <input
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        type="date"
                        min={today}
                        value={formData.validFrom}
                        onChange={(e) => {
                          const newFrom = e.target.value;

                          setFormData((prev) => ({
                            ...prev,
                            validFrom: newFrom,
                            // auto-fix validTill if it becomes invalid
                            validTill:
                              prev.validTill && prev.validTill < newFrom
                                ? newFrom
                                : prev.validTill,
                          }));
                        }}
                        required
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Valid Till
                      </label>
                      <input
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        type="date"
                        min={formData.validFrom || today}
                        value={formData.validTill}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            validTill: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Upload Image{" "}
                      {!formData.imageUrl && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        className="w-full p-2.5 border-2 border-dashed border-gray-300 rounded-md focus:border-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0])
                            setFile(e.target.files[0]);
                        }}
                        required={!formData.imageUrl}
                      />
                    </div>
                    {uploading && (
                      <p className="text-blue-600 text-sm mt-2 flex items-center">
                        <Upload size={14} className="mr-1 animate-pulse" />
                        Uploading image...
                      </p>
                    )}
                    {formData.imageUrl && (
                      <div className="mt-2">
                        <img
                          src={formData.imageUrl}
                          alt="Deal preview"
                          className="h-20 w-20 object-cover rounded-md border border-gray-300"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="isActive"
                      className="text-sm font-medium text-gray-700"
                    >
                      Deal is Active
                    </label>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Deal Items
                      </label>
                      <button
                        type="button"
                        onClick={addDealItem}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Item
                      </button>
                    </div>

                    <div className="space-y-3">
                      {formData.items.map((item, index) => (
                        <div
                          key={index}
                          className="flex gap-2 items-start p-3 border rounded-md bg-gray-50"
                        >
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <select
                              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={item.itemId}
                              onChange={(e) =>
                                updateDealItem(index, "itemId", e.target.value)
                              }
                              required
                            >
                              <option value="">Select Item</option>
                              {menuItems.map((menuItem) => {
                                const selectedIds = getSelectedItemIds(index);
                                const isDisabled =
                                  formData.dealType === DealType.FixedPrice &&
                                  selectedIds.includes(menuItem._id);

                                return (
                                  <option
                                    key={menuItem._id}
                                    value={menuItem._id}
                                    disabled={isDisabled}
                                  >
                                    {menuItem.name}{" "}
                                    {isDisabled ? "(Already selected)" : ""}
                                  </option>
                                );
                              })}
                            </select>

                            <input
                              type="number"
                              min="1"
                              placeholder="Quantity"
                              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={item.quantity}
                              onChange={(e) =>
                                updateDealItem(
                                  index,
                                  "quantity",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                              required
                            />

                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`free-${index}`}
                                checked={
                                  formData.dealType === DealType.BuyXGetY
                                    ? index === 1
                                    : false
                                }
                                disabled={
                                  formData.dealType === DealType.FixedPrice ||
                                  formData.dealType === DealType.Bundle ||
                                  formData.dealType === DealType.PercentageDiscount ||
                                  (formData.dealType === DealType.BuyXGetY && index === 0)
                                }
                                onChange={(e) =>
                                  updateDealItem(index, "isFree", e.target.checked)
                                }
                                className="w-4 h-4 text-blue-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <label
                                htmlFor={`free-${index}`}
                                className="text-sm text-gray-700"
                              >
                                Free
                              </label>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeDealItem(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}

                      {formData.items.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No items added yet. Click "Add Item" to get started.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="deal-form"
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                {editingItem ? "Update Deal" : "Add Deal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deals;
